/**
 * This is the driver for customer invite when invited inside order confirmation.  
 * Each step is its own component that allows them to easily be changed and order swapped around.
 */
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Grid, InputLabel, Paper, Typography } from '@mui/material';

import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';

import AbEnvironment from '../auth/AbEnvironment';
import AccountLinkUpdateMutation from '../landing/AccountLinkUpdateMutation';
import AccountLedgerSummaryFetch from '../account/AccountLedgerSummaryFetch';
import WalletLedgerSummaryFetch from '../account/WalletLedgerSummaryFetch';

import CustomerConfirmSubscriptionStep from './CustomerConfirmSubscriptionStep';
import CustomerConfirmServiceStep from './CustomerConfirmServiceStep';
import CustomerConfirmBudgetStep from './CustomerConfirmBudgetStep';
import CustomerFundingSourceStep from './CustomerFundingSourceStep';
import CustomerAddressAssentStep from './CustomerAddressAssentStep';
import CustomerFinalizeStep from './CustomerFinalizeStep';
import LoadingSpinner from '../ui/LoadingSpinner';

const abEnvironment = new AbEnvironment();

const styles = theme => ({
  
  stepperPaper: {
    borderTopRightRadius: "10px",
    borderTopLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    borderBottomLeftRadius: "10px",
    maxWidth: 600,
    minWidth: 352,
    width: "100%"
  },
  title: {
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: theme.palette.secondary.main,
    
    borderTopRightRadius: "10px",
    borderTopLeftRadius: "10px"
  },
  titleLine: {
    color : "white", 
    fontSize: "21px",
    fontWeight: "500",
    paddingLeft: "16px",
    paddingTop: "24px",
    paddingBottom: "8px"
  },
  stepper: {
    borderBottomRightRadius: "10px",
    borderBottomLeftRadius: "10px",
  },
 
  heading: {
    marginTop: '16px',
    marginLeft: '24px'
  },
  heading2: {
    marginTop: '24px',
    marginLeft: '24px'
  }
});

class AuthorizeOrder extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {

      activeStep: 0,
      skipped: new Set(),
      latestBudgetApproved: false,
      autoTopupsActive: false,

      assetArAuthBalance: null,  // project customer wallet balance, or account balance if no project
      imgHeightOrWidth: 'height'

    };
    this.onImgLoad = this.onImgLoad.bind(this);
  }

  componentDidMount() {

    const params = new URLSearchParams(this.props.location.search);
    let step = params.get('step');
    if (step) {
      let stepInt = parseInt(step, 10);
      this.setState({activeStep: stepInt});
    }

    let theAccount = this.props.account;
    let theProject = this.props.project;
    let theWallet  = null;
    if (theProject != null) {
      theWallet = theProject.walletTByCustomerWalletId;
    }
    
    let latestBudgetApproved = false;
    if (theAccount.useBudget === 0) {
      latestBudgetApproved = true;
    } else {
      if (!theProject) {
        if (theAccount.latestBudgetApproved === 1) {
          latestBudgetApproved = true;
        }
      } else {
        if (theProject.latestBudgetApproved === 1) {
          latestBudgetApproved = true;
        }
      }
    }
    
    let autoTopupsActive = false;
    if (theWallet != null) {
      if (theWallet.autoTopupActive === 1) {
        autoTopupsActive = true;
      }
    } else {
      if (theAccount.autoTopupActive === 1) {
        autoTopupsActive = true;
      }
    }

    if (latestBudgetApproved || autoTopupsActive) {
      this.setState({latestBudgetApproved: latestBudgetApproved, autoTopupsActive: autoTopupsActive}); 
    }

    if (theWallet != null) {
      WalletLedgerSummaryFetch.fetch(theWallet.walletId).toPromise()
      .then(wlsf => {
        this.setState({assetArAuthBalance: wlsf.getwalletledgersummary.assetArAuthBalance});
      })
      .catch(err => {
        //nothing to do....
      });
    } else {
      AccountLedgerSummaryFetch.fetch(theAccount.accountId).toPromise()
      .then(alsf => {
        this.setState({assetArAuthBalance: alsf.getaccountledgersummary.assetArAuthBalance});
      })
      .catch(err => {
        //nothing to do....
      });
    }
  }
 
  onImgLoad({target:img}) {
    if (img.offsetHeight > img.offsetWidth) {
      if (this.state.imgHeightOrWidth !== 'height') {
        this.setState({imgHeightOrWidth: 'height'});
      }
    } else if (img.offsetHeight < img.offsetWidth) {
      if (this.state.imgHeightOrWidth !== 'width') {
        this.setState({imgHeightOrWidth: 'width'});
      }
    } 
  }

  getSteps = (originalSteps, companyAddressPresent, assentPresent, achPresent, ccPresent, subsPresent, servicesPresent, showExpenseWallet, latestBudgetApproved, customerNeedsFunding) => {
    var steps = [];

    if (originalSteps) {
      return originalSteps;
    }

    if (subsPresent) {
      steps.push({text: "Review your subscriptions", component: 'CustomerSubscriptionStep'});
    }

    if (showExpenseWallet && !latestBudgetApproved) {
      steps.push({text: "Set up expense wallet", component: 'CustomerConfirmBudgetStep'});
    }

    if (servicesPresent) {
      steps.push({text: "Review your services", component: 'CustomerServiceStep'});
    }
    if (!companyAddressPresent || ! assentPresent) {
      steps.push({text: "Confirm your contact information", component: 'CustomerAddressAssentStep'});
    }
    if ((!achPresent && !ccPresent) && customerNeedsFunding) {
      steps.push({text: "Link your payment methods to authorize", component: 'CustomerFundingSourceStep'});
    }
    if (!showExpenseWallet || latestBudgetApproved) {
      steps.push({text: "Finalize", component: 'CustomerFinalizeStep'});
    } else {
      steps.push({text: "Authorize", component: 'CustomerFinalizeStep'});
    }

    localStorage.setItem('authorizeSteps', JSON.stringify(steps));

    return steps;
  }
  
  getStepContent = (step, component, theAuth, theAccount, theProject) => {
    if (component === 'CustomerSubscriptionStep') {
      return (
        <CustomerConfirmSubscriptionStep auth={theAuth}
                                          account={theAccount}
                                          project={theProject}
                                          assignedStep={step}
                                          activeStep={this.state.activeStep}
                                          handleBack={this.handleBack}
                                          handleNext={this.handleNext}/>
      );
    } else if (component === 'CustomerServiceStep') {
      return (
        <CustomerConfirmServiceStep auth={theAuth}
                                    account={theAccount}
                                    assignedStep={step}
                                    activeStep={this.state.activeStep}
                                    handleBack={this.handleBack}
                                    handleNext={this.handleNext}/>
      );  
    } else if (component === 'CustomerConfirmBudgetStep') {
      return (
        <CustomerConfirmBudgetStep auth={theAuth}
                                    account={theAccount}
                                    project={theProject}
                                    assignedStep={step}
                                    activeStep={this.state.activeStep}
                                    handleBack={this.handleBack}
                                    handleNext={this.handleNext}/>
      );
    } else if (component === 'CustomerAddressAssentStep') {
      return (
        <CustomerAddressAssentStep auth={theAuth}
                                    account={theAccount}
                                    history={this.props.history}
                                    assignedStep={step}
                                    activeStep={this.state.activeStep}
                                    handleBack={this.handleBack}
                                    handleNext={this.handleNext}/>
      );
    } else if (component === 'CustomerFundingSourceStep') {
      return (
        <CustomerFundingSourceStep auth={theAuth}
                                    account={theAccount}
                                    history={this.props.history}
                                    customerApp={this.props.customerApp}
                                    location={this.props.location}
                                    assignedStep={step}
                                    activeStep={this.state.activeStep}
                                    handleBack={this.handleBack}
                                    handleNext={this.handleNext}/>
      );
    } else if (component === 'CustomerFinalizeStep') {
      return (
        <CustomerFinalizeStep auth={theAuth}
                              account={theAccount}
                              project={theProject}
                              assetArAuthBalance={this.state.assetArAuthBalance}
                              history={this.props.history}
                              assignedStep={step}
                              activeStep={this.state.activeStep}
                              handleBack={this.handleBack}
                              handleNext={this.handleComplete}/>
      );
    } else {
      return (
        <div>Unknown</div>
      );
    }
  }

  handleNext = (forceStep) => {
    if (forceStep) {
      this.setState({activeStep: forceStep});
    } else {
      let newStep = this.state.activeStep;
      newStep++;
      this.setState({activeStep: newStep});
    }
  }

  handleBack = () => {
    let newStep = this.state.activeStep;
    newStep--;
    this.setState({activeStep: newStep});
  }


  /**
   * When a customer authorizes the order, we need to call a backend API to mark the order
   * as authorized.  Currently we've got two different API operations depending on whether
   * or not projects are active.
   * 
   * As the customer isn't making any choices, but is just assenting, no configuration changes
   * need to be passed into this function; we'll just use the props values to determine
   * the account (and project id available).
   */
  handleComplete = () => {
    const { callPartnerApi, logError } = this.props.auth;
    let aPjwt = abEnvironment.getPjwt();

    let theAccount = this.props.account;
    let theProject = this.props.project;
    let theCustomerWallet = null;
    if (theProject != null) {
      theCustomerWallet = theProject.walletTByCustomerWalletId;
    }

    let apiError = false;
    let redirectTo = '/myapps';

    // JD 20231115 - always go to the confirm step.
    let callConfirmStep = false;
    if (this.state.latestBudgetApproved === false) {
      callConfirmStep = true;
    } else {
      callConfirmStep = true;
    }

    if (callConfirmStep === true) {
      let budgetApprovedCount = theAccount.budgetApprovedCount;
      if (theProject != null) {
        budgetApprovedCount = theProject.budgetApprovedCount;
      }
      budgetApprovedCount++;

      let acctId =  abEnvironment.getUserAttribute('a_id')
      let userId =  abEnvironment.getUserAttribute('u_id')

      //update latest budget approved 
      Promise.resolve()  
      .then(() => {
        let payload;

        if (theProject != null) {
          payload = {
            operation: "confirmProjectSubscriptionOrder",
            params: {
              pJwt:                 aPjwt,
              accountId:            acctId,
              accountName:          theAccount.accountName,
              projectId:            theProject.projectId,
              pAccountId:           theProject.accountTByResellerAccountId.accountId,
              latestBudgetApproved: 1,
              rAutoTopupAmount:     theCustomerWallet.rAutoTopupAmount,
              rAutoTopupThreshold:  theCustomerWallet.rAutoTopupThreshold,
              rMaxMonthlyBudget:    theCustomerWallet.rMaxMonthlyBudget,
              rMaxCharge:           theCustomerWallet.rMaxCharge,
              rPreApproveSubs:      theProject.rPreApproveSubs,
              budgetApprovedCount:  budgetApprovedCount,
              sendNotification:     true,
              confirmUserId:        userId
            }
          };
        } else {
          payload = {
            operation: "confirmSubscriptionOrder",
            params: {
              pJwt:                 aPjwt,
              accountId:            acctId,
              accountName:          theAccount.accountName,
              pAccountId:           theAccount.accountTByParentId.accountId,
              latestBudgetApproved: 1,
              rAutoTopupAmount:     theAccount.rAutoTopupAmount,
              rAutoTopupThreshold:  theAccount.rAutoTopupThreshold,
              rMaxMonthlyBudget:    theAccount.rMaxMonthlyBudget,
              rMaxCharge:           theAccount.rMaxCharge,
              rPreApproveSubs:      theAccount.rPreApproveSubs,
              budgetApprovedCount:  budgetApprovedCount,
              sendNotification:     true,
              confirmUserId:        userId
            }
          };
        }
 
        return callPartnerApi('appbind/generic', 'POST', payload);

     }).then(resp => {
       return resp.json();
     }).then((jresp) => {
        if (jresp.error === true) {
          apiError = true;
          console.log('there was an api error: ' + JSON.stringify(jresp.error));
        }

        if (jresp.redirectTo != null) {
          redirectTo = jresp.redirectTo;
        }

        if (apiError === false) {
          console.log('change the account link target');
          let mlId = localStorage.getItem('ml');
          //Since user completed steps, update the account link table so that it does not try and come here again.
          return AccountLinkUpdateMutation.commit(mlId, "/myapps");
        }
      })
      .then(response => {
        //cleanup
        if (apiError === false) {
          localStorage.removeItem('ml');
          localStorage.removeItem('paymentReturn');
          localStorage.removeItem('plaid');
          localStorage.removeItem('authorizeSteps');
          //go to subscription with no back button
          this.props.history.push (redirectTo);
          window.location.reload();
        }
      }).catch(err => {
        console.log('problem: ' + err + ', ' + JSON.stringify(err));
        logError('Exception in AuthorizeOrder.js: ' + err);
      });
    } else {  
      //auto topup not active, just update account link.
      let mlId = localStorage.getItem('ml');
      //Since user completed steps, update the account link table so that it does not try and come here again.
      AccountLinkUpdateMutation.commit(mlId, "/myapps")
      .then(response => {
        //cleanup
        localStorage.removeItem('ml');
        localStorage.removeItem('paymentReturn');
        localStorage.removeItem('plaid');
        localStorage.removeItem('authorizeSteps');
        //go to subscription with no back button
        this.props.history.push (redirectTo)
        window.location.reload();
      }).catch(err => {
        console.log('problem: ' + err);
        logError('Exception in AuthorizeOrder.js: ' + err);
      });
    }
  }

  // TODO: deprecated, remove when project version is removed.
  handleProjectComplete = () => {

    let account = this.props.account;
    let theProject = account.projectTsByCustomerAccountId.nodes[0];
    let theWallet = theProject.walletTByCustomerWalletId;

    const { callPartnerApi, logError } = this.props.auth;
    let aPjwt = abEnvironment.getPjwt();

    let apiError = false;

    if (!this.state.latestBudgetApproved) {
      let budgetApprovedCount = theProject.budgetApprovedCount;
      budgetApprovedCount++;

      let acctId =  abEnvironment.getUserAttribute('a_id')
      let userId =  abEnvironment.getUserAttribute('u_id')

      //update latest budget approved 
      Promise.resolve()  
      .then(() => {
 
        let payload = {
          operation: "confirmProjectSubscriptionOrder",
          params: {
            pJwt:        aPjwt,
            accountId:   acctId,
            accountName: account.accountName,
            projectId:   theProject.projectId,
            pAccountId:  theProject.accountTByResellerAccountId.accountId,
            latestBudgetApproved: 1,
            rAutoTopupAmount: theWallet.rAutoTopupAmount,
            rAutoTopupThreshold: theWallet.rAutoTopupThreshold,
            rMaxMonthlyBudget: theWallet.rMaxMonthlyBudget,
            rMaxCharge: theWallet.rMaxCharge,
            rPreApproveSubs: theProject.rPreApproveSubs,
            budgetApprovedCount: budgetApprovedCount,
            sendNotification: true,
            confirmUserId: userId
          }
        };
 
       return callPartnerApi('appbind/generic',
                      'POST',
                      payload)
     }).then(resp => {
       return resp.json();
     }).then((jresp) => {
        if (jresp.error === true) {
          apiError = true;
        }

        if (apiError === false) {
          let mlId = localStorage.getItem('ml');
          //Since user completed steps, update the account link table so that it does not try and come here again.
          AccountLinkUpdateMutation.commit(mlId, "/myapps")  
        }
      })
            
      .then(response => {
        //cleanup
        if (apiError === false) {
          localStorage.removeItem('ml');
          localStorage.removeItem('paymentReturn');
          localStorage.removeItem('plaid');
          localStorage.removeItem('authorizeSteps');
          //go to subscription with no back button
          let subscriptionURL = '/myapps'
          this.props.history.push (subscriptionURL)
          window.location.reload();
        }
      }).catch(err => {
        logError('Exception in AuthorizeOrder.js: ' + err);
      });

    } else {  

      //auto topup not active, just update account link.
      
      let mlId = localStorage.getItem('ml');
      //Since user completed steps, update the account link table so that it does not try and come here again.
      AccountLinkUpdateMutation.commit(mlId, "/myapps")
      .then(response => {
  
        //cleanup
        localStorage.removeItem('ml');
        localStorage.removeItem('paymentReturn');
        localStorage.removeItem('plaid');
        localStorage.removeItem('authorizeSteps');
        //go to subscription with no back button
        let subscriptionURL = '/myapps'
        this.props.history.push (subscriptionURL)
        window.location.reload();
      }).catch(err => {
        logError('Exception in AuthorizeOrder.js: ' + err);
      });
    }
  }

  render() {
    const { classes } = this.props;

    // Wait until onMount is complete...
    if (!this.state.assetArAuthBalance) {
      return (
        <LoadingSpinner />
      );
    }

    let theAccount = this.props.account;
    let theProject = this.props.project;
    let theCustomerWallet = null;
    if (theProject != null) {
      theCustomerWallet = theProject.walletTByCustomerWalletId;
    }

    //here we get some base order subscription / repeating reseller knowledge to see if stuff is set up.  This is used to hide unneccessary steps.
    let companyAddressPresent = false;
    if (theAccount.address1 !== null) {
      companyAddressPresent = true;
    }

    let assentPresent = false;
    let user = theAccount.userTsByAccountId.nodes[0];
    if (user.termsAcceptDate !== null && user.userConfirmDate !== null) {
      assentPresent = true
    }

    let paymentSources = theAccount.paymentSourceTsByAccountId.nodes;

    let achPresent = false;
    let ccPresent = false;
    for (let i = 0; i < paymentSources.length; i++) {
      let paymentSource = paymentSources[i];
      if (paymentSource.psType === 1) {
        achPresent = true;
      }
      if (paymentSource.psType === 0) {
        ccPresent = true;
      }
    }

    let showExpenseWallet = false;
    let latestBudgetApproved = false;
    if (theCustomerWallet != null) {
      //account uses projects and wallets
      if (theAccount.useBudgets >= 1) {
        showExpenseWallet = true;
  
        // Override this - do not show budgets if request attributes are null
        if (theCustomerWallet.rAutoTopupAmount == null) {
          showExpenseWallet = false;
        }
      } 
      if (theAccount.useBudgets === 0) {
        latestBudgetApproved = true;
      } else if (theProject.latestBudgetApproved === 1) {
        latestBudgetApproved = true;
      }
  
    } else {
      //account does not use projects
      if (theAccount.useBudgets >= 1) {
        showExpenseWallet = true;
  
        // Override this - do not show budgets if request attributes are null
        if (theAccount.rAutoTopupAmount == null) {
          showExpenseWallet = false;
        }
      } 
      if (theAccount.useBudgets === 0) {
        latestBudgetApproved = true;
      } else if (theAccount.latestBudgetApproved === 1) {
        latestBudgetApproved = true;
      }
    }

    let subscriptionsPresent = false;
    let customerExpensedSubs = false;
    for (let i = 0; i < theAccount.customerAppTsByCustomerAccountId.nodes.length; i++) {
      let sub = theAccount.customerAppTsByCustomerAccountId.nodes[i];
      if (sub.appStatus === 2) {
        continue;
      }
      if (sub.paymentOrder === 0 || sub.paymentOrder === 1) {
        customerExpensedSubs = true;
      }
      subscriptionsPresent = true;
    }

    let servicesPresent = false;
    for (let i = 0; i < theAccount.customerServiceTsByCustomerAccountId.nodes.length; i++) {
      let service = theAccount.customerServiceTsByCustomerAccountId.nodes[i];
      //exclude cancelled services
      if (service.serviceStatus !== 2) {
        if (service.billingFrequency === 0) {
          if (service.soAccepted === 0) {
            servicesPresent = true;
            break;
          }
        } else {
          servicesPresent = true;
          break;
        }
      }
    }

    //see if there is a reason for a customer to have a payment source.  If there is 1 service or customer expensed subs
    let customerNeedsFunding = false;
    if (customerExpensedSubs || servicesPresent) {
      customerNeedsFunding = true;
    }

    var originalSteps = null;
    var storageSteps = localStorage.getItem('authorizeSteps');
    if (storageSteps) {
      originalSteps = JSON.parse(storageSteps);
    }

    let reseller = theAccount.accountTByParentId;
    if (theProject) {
      reseller = theProject.accountTByResellerAccountId;
    }

    return (
      <Paper className={classes.stepperPaper}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <div className={classes.title}>
              <Typography variant="h4" className={classes.titleLine} >
                Authorize order
              </Typography>
            </div>
          </Grid>
          <Grid item xs={10}>
            <Grid container className={classes.heading} spacing={0} direction="row" justifyContent="space-between">

                <Grid item >
                  <InputLabel shrink style={{marginBottom:6}}>Purchasing agent</InputLabel>
                  <Typography variant="body2">
                    <strong>{reseller.accountName}</strong>
                  </Typography>
                  <Typography variant="body2">
                    {reseller.contactFirstName} {reseller.contactLastName}
                  </Typography>
                  <Typography variant="body2">
                    {reseller.contactEmail}
                  </Typography>
                </Grid>

                { reseller.logo != null &&
                <Grid item>          
                  {this.state.imgHeightOrWidth === 'width' &&
                  <img alt="Logo" width="52px" onLoad={this.onImgLoad} src={reseller.logo}/>}
                  {this.state.imgHeightOrWidth === 'height' &&
                  <img alt="Logo" height="52px" onLoad={this.onImgLoad} src={reseller.logo}/>}
                </Grid>}
            </Grid>   
          </Grid>
          <Grid item xs={12}>
            <Grid container className={classes.heading}  spacing={0} direction="row" justifyContent="space-between">
              <Grid item >
                <InputLabel shrink style={{marginBottom:6}}>Customer</InputLabel>
                <Typography variant="body2">
                  <strong>{theAccount.accountName}</strong>
                </Typography>
                <Typography variant="body2">
                  {theAccount.contactFirstName} {theAccount.contactLastName}
                </Typography>
                <Typography variant="body2">
                  {theAccount.contactEmail}
                </Typography>
              </Grid>
            </Grid>   
          </Grid>

          {!subscriptionsPresent &&
          <Grid item xs={12}>
            <Grid container  className={classes.heading2}  spacing={0} direction="row" justifyContent="space-between">
              <Grid item >
                <Typography >
                <strong>Allow {reseller.accountName} to purchase your subscriptions.</strong>
                </Typography>
              </Grid>
            </Grid>   
          </Grid>}

          <Grid item xs={12}>
            <Stepper activeStep={this.state.activeStep} orientation="vertical" className={classes.stepper} style={{margin: 24}}>
              {this.getSteps(originalSteps, companyAddressPresent, assentPresent, achPresent, ccPresent, subscriptionsPresent, servicesPresent, showExpenseWallet, latestBudgetApproved, customerNeedsFunding).map((element, index) => (
                <Step key={element.text} expanded={this.state.activeStep >= index}>
                  <StepLabel>{element.text}</StepLabel>
                  <StepContent>
                    {this.getStepContent(index, element.component, this.props.auth, this.props.account, this.props.project)}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Grid>
        </Grid>
      </Paper>
    );
  }
}

export default withStyles(styles)(AuthorizeOrder);